import { AuthUser } from "auth/types";

export type IdentityState = "signed-out" | "bound" | "mismatched";

/**
 * Whether this tab's stored identity actually belongs to the browser's current
 * Firebase user.
 *
 * This exists because Firebase's user is ORIGIN-SHARED while redux is per-tab. A
 * sign-in anywhere in the browser changes `user` in every tab, but only the tab
 * that performed it writes `authUid`. So:
 *
 *   "signed-out"  no Firebase user — show the login form.
 *   "bound"       authUid matches — this tab legitimately represents this user.
 *   "mismatched"  a Firebase user this tab's stored identity does not belong to.
 *                 Either another tab signed someone else in, or this tab never
 *                 established an identity of its own. Both must fail closed: the
 *                 tab may neither act as the new user nor offer a login form
 *                 (which is how a second principal got in to begin with).
 *
 * Note `authUid !== user.uid` deliberately catches `undefined`, so a tab that
 * never signed in but inherited a Firebase user is mismatched, not signed-out.
 *
 * Do NOT "fix" that by treating a missing authUid as signed-out (c8406ab did, and
 * was reverted): it hands a working login form to a tab that already holds a live
 * session, which is the CRYP-4918 entry point, and it breaks the guard test that
 * covers it. The recoverability problem it appears to solve is not here — it
 * belongs to SessionChangedScreen, which must offer a sign-out, because reload
 * only resolves a mismatch whose authUid is stale in memory yet correct in
 * localStorage. Reload on the other two causes loops forever (CRYP-4925).
 */
export function identityState(
  user: AuthUser | null,
  authUid: string | null | undefined,
): IdentityState {
  if (!user) return "signed-out";
  if (authUid !== user.uid) return "mismatched";
  return "bound";
}

/**
 * The role check, applied only once identity is "bound".
 *
 * Kept separate from identityState because a bound tab can legitimately have no
 * userType yet: sign-in dispatches authUid atomically with the auth state, while
 * userType is dispatched a moment later, and the mustChangePassword flow reaches
 * /change-password before a userType is ever set. Folding the two together would
 * classify both of those as mismatched and break them.
 */
export function isAuthorized(
  userType: string | null | undefined,
  allowedUserTypes: string[],
): boolean {
  if (!userType) return false;
  return allowedUserTypes.includes(userType);
}
